<template>
  <div class="mindmap-container mt-5 pt-5">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
        <li class="breadcrumb-item active" aria-current="page">Mindmap</li>
      </ol>
    </nav>

    <!-- Enhanced Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <button
          class="toolbar-btn toolbar-btn--primary"
          @click="handleSaveFlow"
          title="Save Flow (Ctrl+S)"
          :disabled="isSavingFlow"
        >
          <i :class="isSavingFlow ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i>
          <span>{{ isSavingFlow ? "Saving..." : "Save Flow" }}</span>
        </button>
        <button
          class="toolbar-btn toolbar-btn--ghost"
          @click="handleBackToList"
          title="Back to Flows List"
        >
          <i class="fa fa-arrow-left"></i>
          <span>Flows List</span>
        </button>
        <div class="toolbar-divider"></div>
        <button
          class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
          @click="handleExport"
          title="Export Mindmap"
        >
          <i class="fa fa-download"></i>
        </button>
        <button
          class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
          @click="handleImport"
          title="Import Mindmap"
        >
          <i class="fa fa-upload"></i>
        </button>
        <button
          class="toolbar-btn toolbar-btn--danger-ghost toolbar-btn--icon"
          @click="handleClearCanvas"
          title="Clear Canvas"
        >
          <i class="fa fa-trash"></i>
        </button>
      </div>
      <div class="toolbar-center">
        <span v-if="currentFlowId || flowTitle" class="flow-title-badge">
          <i class="fa fa-sitemap me-1"></i>
          {{ flowTitle || "Untitled Flow" }}
        </span>
      </div>
      <div class="toolbar-right">
        <button
          class="toolbar-btn toolbar-btn--run me-2"
          @click="handleRunPromptClick"
          :disabled="!finalPrompt || isRunningPrompt"
          title="Run prompt with AI"
        >
          <i :class="isRunningPrompt ? 'fa fa-spinner fa-spin' : 'fa fa-play'"></i>
          <span>{{ isRunningPrompt ? "Running..." : "Run Prompt" }}</span>
        </button>
        <div class="stats-group">
          <div class="stat-chip stat-chip--blue">
            <i class="fa fa-circle-nodes"></i>
            <span>{{ nodeCount }}</span>
            <small>Nodes</small>
          </div>
          <div class="stat-chip stat-chip--green">
            <i class="fa fa-link"></i>
            <span>{{ connectionCount }}</span>
            <small>Links</small>
          </div>
          <div class="stat-chip stat-chip--purple">
            <i class="fa fa-search-plus"></i>
            <span>{{ Math.round(zoom * 100) }}%</span>
            <small>Zoom</small>
          </div>
        </div>
      </div>
    </div>

    <div class="mindmap-wrapper" :class="{ 'is-fullscreen': isFullScreen }">
      <!-- Canvas Area Wrapper -->
      <div class="canvas-wrapper" ref="canvasWrapper">
        <!-- Canvas Area -->
        <div
          ref="canvasContainer"
          class="canvas-area"
          :style="{
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            transformOrigin: 'top left',
            cursor: isPanning ? 'grabbing' : (connectingFrom ? 'crosshair' : 'default'),
          }"
          @dragover="handleDragOver"
          @drop="handleDrop"
          @click="handleCanvasClick"
          @mousedown="handlePanStart"
        >
          <canvas ref="canvas" id="mindmap-canvas"></canvas>

          <!-- Empty State -->
          <div v-if="nodeCount === 0" class="canvas-placeholder">
            <div class="placeholder-icon">
              <i class="fa fa-diagram-project"></i>
            </div>
            <h5>Start Building Your Mindmap</h5>
            <p>Drag variables or categories from the right panel onto this canvas</p>
            <div class="placeholder-hints">
              <div class="hint-item"><kbd>Two-finger drag</kbd> Pan canvas</div>
              <div class="hint-item"><kbd>Ctrl</kbd>+<kbd>Scroll</kbd> Zoom</div>
              <div class="hint-item"><kbd>Left-drag</kbd> Move nodes</div>
            </div>
          </div>

          <!-- Nodes -->
          <div
            v-for="node in allNodes"
            :key="node.id"
            class="mindmap-node"
            :class="{
              selected: selectedNodeId === node.id,
              root: node.isRoot,
              'connect-source': connectingFrom === node.id,
              'connect-target': connectingFrom && connectingFrom !== node.id,
            }"
            :style="{
              left: `${node.x}px`,
              top: `${node.y}px`,
              width: `${node.width}px`,
              minHeight: `${node.height}px`,
              backgroundColor: node.color,
            }"
            @mousedown="handleNodeMouseDown($event, node.id)"
            @click.stop="handleNodeClick(node.id)"
            @dragover.prevent="handleNodeDragOver($event, node.id)"
            @drop.stop="handleNodeDrop($event, node.id)"
            @mouseup="handleNodeMouseUp($event, node.id)"
          >
            <!-- Connection Anchors - Always visible -->
            <!-- Left anchor -->
            <div
              class="connection-anchor anchor-left"
              @mousedown.stop="handleAnchorMouseDown($event, node.id)"
              title="Drag to connect nodes"
            >
              <div class="anchor-dot"></div>
            </div>
            <!-- Right anchor -->
            <div
              class="connection-anchor anchor-right"
              @mousedown.stop="handleAnchorMouseDown($event, node.id)"
              title="Drag to connect nodes"
            >
              <div class="anchor-dot"></div>
            </div>
            <div class="node-header">
              <i
                class="fa fa-grip-vertical drag-handle"
                :style="{ color: node.forecolor || '#212529' }"
              ></i>
              <span
                class="node-title"
                :style="{ color: node.forecolor || '#212529' }"
                >{{ node.title }}</span
              >
              <div class="node-actions">
                <button
                  v-if="
                    isCategory(node) &&
                    node.children &&
                    node.children.length > 0
                  "
                  class="btn btn-sm btn-link text-primary node-collapse"
                  @click.stop="handleToggleCollapse(node.id)"
                  :title="
                    node.collapsed ? 'Expand children' : 'Collapse children'
                  "
                >
                  <i
                    :class="
                      node.collapsed
                        ? 'fa fa-chevron-right'
                        : 'fa fa-chevron-down'
                    "
                  ></i>
                </button>
                <button
                  v-if="isCategory(node)"
                  class="btn btn-sm btn-link text-success node-add-child"
                  @click.stop="showAddChildModal(node.id)"
                  title="Add child node"
                >
                  <i class="fa fa-plus"></i>
                </button>
                <button
                  v-if="node.isDeletable !== false"
                  class="btn btn-sm btn-link text-danger node-delete"
                  @click.stop="handleDeleteNode(node.id)"
                  title="Delete node"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </div>
            <div
              v-if="node.description"
              class="node-description"
              :style="{ color: node.forecolor || '#6c757d' }"
            >
              {{ node.description }}
            </div>
            <div
              v-if="
                node.slug ||
                (node.collapsed && node.children && node.children.length > 0)
              "
              class="node-footer"
            >
              <span v-if="node.slug" class="badge bg-secondary">{{
                node.slug
              }}</span>
              <span
                v-if="
                  node.collapsed && node.children && node.children.length > 0
                "
                class="badge bg-info ms-1"
              >
                <i class="fa fa-layer-group me-1"></i
                >{{ getCollapsedVariableCount(node) }} variable{{
                  getCollapsedVariableCount(node) !== 1 ? "s" : ""
                }}
                hidden
              </span>
            </div>
          </div>

          <!-- SVG for connections -->
          <svg class="connections-layer">
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 10 3, 0 6" fill="#6c757d" />
              </marker>
            </defs>
            <g v-for="connection in allConnections" :key="connection.id">
              <!-- Invisible wider path for easier clicking -->
              <path
                :d="getConnectionPath(connection)"
                stroke="transparent"
                stroke-width="12"
                fill="none"
                class="connection-hit-area"
                @click.stop="handleConnectionClick(connection.id)"
              />
              <!-- Visible path -->
              <path
                :d="getConnectionPath(connection)"
                :stroke="
                  selectedConnectionId === connection.id ? '#007bff' : '#6c757d'
                "
                :stroke-width="selectedConnectionId === connection.id ? 3 : 2"
                fill="none"
                marker-end="url(#arrowhead)"
                class="connection-path"
                @click.stop="handleConnectionClick(connection.id)"
              />
            </g>
          </svg>
        </div>

        <!-- Canvas Controls Panel (outside transformed area) -->
        <div class="canvas-controls">
          <div class="controls-group">
            <button class="ctrl-btn" @click="handleZoomIn" title="Zoom In">
              <i class="fa fa-plus"></i>
            </button>
            <div class="zoom-level-display" @click="handleResetZoom" title="Click to reset zoom">
              {{ Math.round(zoom * 100) }}%
            </div>
            <button class="ctrl-btn" @click="handleZoomOut" title="Zoom Out">
              <i class="fa fa-minus"></i>
            </button>
          </div>
          <div class="controls-separator"></div>
          <div class="controls-group">
            <button class="ctrl-btn" @click="handleResetView" title="Reset View">
              <i class="fa fa-house"></i>
            </button>
            <button
              class="ctrl-btn"
              :class="{ 'ctrl-btn--active': isFullScreen }"
              @click="handleFitToScreen"
              :title="isFullScreen ? 'Exit Fullscreen' : 'Fit to Screen'"
            >
              <i :class="isFullScreen ? 'fa fa-compress' : 'fa fa-expand'"></i>
            </button>
          </div>
          <div class="controls-separator"></div>
          <div class="gesture-hint">
            <div class="hint-row"><i class="fa fa-hand-pointer"></i> Drag to pan</div>
            <div class="hint-row"><i class="fa fa-search-plus"></i> Ctrl+Scroll zoom</div>
          </div>
        </div>
      </div>

      <!-- Right Side Panel -->
      <MindmapSidebar
        ref="sidebarRef"
        :selected-node="selectedNode"
        :ai-response="aiResponse"
        :is-running-prompt="isRunningPrompt"
        :response-token-count="responseTokenCount"
        :selected-model="selectedModel"
        @close-properties="handleDeselectNode"
        @add-child-node="handleAddChildNodeFromPanel"
        @run-prompt="runFinalPrompt"
        @stop-prompt="stopPromptRun"
        @clear-ai-response="clearAiResponse"
        @copy-ai-response="copyAiResponse"
      />
    </div>

    <!-- Final Prompt Panel -->
    <div class="final-prompt-panel">
      <div class="final-prompt-header">
        <div class="prompt-header-left">
          <div class="prompt-icon"><i class="fa fa-wand-magic-sparkles"></i></div>
          <div>
            <h6 class="mb-0">Final Prompt</h6>
            <small class="text-muted">Auto-generated from canvas nodes</small>
          </div>
        </div>
        <div class="prompt-header-actions">
          <span v-if="finalPrompt" class="prompt-char-count">{{ finalPrompt.length }} chars</span>

          <button
            class="toolbar-btn toolbar-btn--light"
            @click="copyFinalPrompt"
            title="Copy prompt to clipboard"
          >
            <i class="fa fa-copy"></i>
            <span>Copy</span>
          </button>
        </div>
      </div>
      <div class="final-prompt-content">
        <pre v-if="finalPrompt" class="final-prompt-text">{{ finalPrompt }}</pre>
        <div v-else class="prompt-empty-state">
          <i class="fa fa-diagram-project fa-2x mb-2"></i>
          <p>Add nodes to the canvas to generate the final prompt</p>
        </div>
      </div>
    </div>

    <!-- AI Response Panel (Moved to Sidebar) -->


    <!-- Add Child Node Modal -->
    <div
      v-if="showAddNodeModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
      @click.self="closeAddNodeModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Add Child Node</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeAddNodeModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitNewNode">
              <div class="mb-3">
                <label for="nodeTitle" class="form-label">Title *</label>
                <input
                  id="nodeTitle"
                  v-model="newNodeData.title"
                  type="text"
                  class="form-control"
                  placeholder="Enter node title"
                  required
                />
              </div>
              <div class="mb-3">
                <label for="nodeSlug" class="form-label">Slug *</label>
                <input
                  id="nodeSlug"
                  v-model="newNodeData.slug"
                  type="text"
                  class="form-control"
                  placeholder="Enter slug (e.g., node-title)"
                  required
                />
              </div>
              <div class="mb-3">
                <label for="nodeDescription" class="form-label"
                  >Description</label
                >
                <textarea
                  id="nodeDescription"
                  v-model="newNodeData.description"
                  class="form-control"
                  rows="3"
                  placeholder="Enter description (optional)"
                ></textarea>
              </div>
              <div class="mb-3">
                <label for="nodeCategory" class="form-label">Category *</label>
                <select
                  id="nodeCategory"
                  v-model="newNodeData.prompt_category_id"
                  class="form-select"
                  disabled
                  required
                >
                  <option value="">Select a category</option>
                  <option
                    v-for="category in categories"
                    :key="category._id"
                    :value="category._id"
                  >
                    {{ category.title }}
                  </option>
                </select>
                <small class="text-muted"
                  >Auto-selected from parent category node</small
                >
              </div>
              <div class="mb-3">
                <label class="form-label">Type</label>
                <select v-model="newNodeData.type_id" class="form-select">
                  <option value="">Select a type (optional)</option>
                  <option
                    v-for="type in variableTypes"
                    :key="type._id"
                    :value="type._id"
                  >
                    {{ type.title }}
                  </option>
                </select>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeAddNodeModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="submitNewNode"
              :disabled="isCreatingNode"
            >
              <i class="fa fa-plus me-1"></i>
              {{ isCreatingNode ? "Creating..." : "Create Node" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import MindmapSidebar from "@/components/pms/admin/mindmap/MindmapSidebar.vue";
import { useMindmapStore, type Variable } from "@/store/mindmap";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";

const route = useRoute();
const router = useRouter();
const mindmapStore = useMindmapStore();
const canvas = ref<HTMLCanvasElement | null>(null);
const canvasContainer = ref<HTMLDivElement | null>(null);
const canvasWrapper = ref<HTMLDivElement | null>(null);
const sidebarRef = ref<any>(null);

// Flow management state
const currentFlowId = ref<string | null>(null);
const flowTitle = ref<string>("");
const flowDescription = ref<string>("");
const isSavingFlow = ref(false);
const isLoadingFlow = ref(false);

// Add child node modal state
const showAddNodeModal = ref(false);
const parentNodeIdForNewChild = ref<string | null>(null);
const isCreatingNode = ref(false);
const categories = ref<any[]>([]);
const variableTypes = ref<any[]>([]);
const newNodeData = ref({
  title: "",
  slug: "",
  description: "",
  prompt_category_id: "",
  type_id: "",
  filterable: false,
  visible_on_card: false,
});

// Drag state
const draggedNodeId = ref<string | null>(null);
const dragOffset = ref({ x: 0, y: 0 });

// Zoom state
const zoom = ref(1);
const minZoom = 0.25;
const maxZoom = 2.5;
const zoomStep = 0.1;

// Pan state
const panX = ref(0);
const panY = ref(0);
const isPanning = ref(false);
const panStart = ref({ x: 0, y: 0 });

// Connection state (simplified)
const connectingFrom = ref<string | null>(null);
const selectedConnectionId = ref<string | null>(null);

// Full screen state
const isFullScreen = ref(false);

// ── OpenAI / Run Prompt state ─────────────────────────────────────────────────
const selectedModel = ref("gpt-5.1");
const isRunningPrompt = ref(false);
const aiResponse = ref("");
const responseTokenCount = ref(0);
const aiResponseContent = ref<HTMLDivElement | null>(null);
let abortController: AbortController | null = null;

// Watch fullscreen state to handle body scroll
watch(isFullScreen, (val) => {
  if (val) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
});

// Computed
const allNodes = computed(() => {
  const nodes = mindmapStore.allNodes;
  const visibleNodes: any[] = [];

  // Helper to check if node is a category
  const isCategoryNode = (node: any): boolean => {
    return node.description && node.description.includes("Category with");
  };

  // Helper to check if a node should be hidden
  const shouldHideNode = (node: any): boolean => {
    // Never hide if node itself is a category
    if (isCategoryNode(node)) {
      return false;
    }

    // Check if any parent category is collapsed
    let currentNode = node;
    while (currentNode.parentId) {
      const parent = nodes.find((n) => n.id === currentNode.parentId);
      if (!parent) break;

      // If parent is a collapsed category, hide this variable node
      if (isCategoryNode(parent) && parent.collapsed) {
        return true;
      }

      currentNode = parent;
    }
    return false;
  };

  // Filter nodes
  for (const node of nodes) {
    if (!shouldHideNode(node)) {
      visibleNodes.push(node);
    }
  }

  return visibleNodes;
});
const allConnections = computed(() => {
  // Filter connections to only show those between visible nodes
  const visibleNodeIds = new Set(allNodes.value.map((n: any) => n.id));
  return Array.from(mindmapStore.connections.values()).filter(
    (conn) =>
      visibleNodeIds.has(conn.sourceId) && visibleNodeIds.has(conn.targetId)
  );
});
const selectedNodeId = computed(() => mindmapStore.selectedNodeId);
const nodeCount = computed(() => mindmapStore.nodeCount);
const connectionCount = computed(() => mindmapStore.connectionCount);

// Generate final prompt from all nodes hierarchically
const finalPrompt = computed(() => {
  const nodes = allNodes.value;
  if (nodes.length === 0) return "";

  const lines: string[] = [];
  const processedNodes = new Set<string>();

  // Helper function to check if node is a category
  const isCategoryNode = (node: any) => {
    return node.description && node.description.includes("Category with");
  };

  // Helper function to check if node is start node
  const isStartNode = (node: any) => {
    return (
      node.id === "start_node" ||
      (node.variableId === "start" && node.isDeletable === false)
    );
  };

  // Helper function to process a node and its children recursively
  const processNode = (nodeId: string, depth: number = 0) => {
    if (processedNodes.has(nodeId)) return;
    processedNodes.add(nodeId);

    const node = mindmapStore.getNodeById(nodeId);
    if (!node) return;

    // Only add start node description, skip category nodes
    if (isStartNode(node)) {
      if (node.description) {
        lines.push(node.description);
      }
    } else if (!isCategoryNode(node)) {
      // For variable nodes, add title and description
      if (node.title) {
        lines.push(node.title);
      }
      if (node.description) {
        lines.push(node.description);
      }
    }

    // Process children in order (including children of category nodes)
    if (node.children && node.children.length > 0) {
      node.children.forEach((childId) => {
        processNode(childId, depth + 1);
      });
    }
  };

  // Start with root nodes (nodes without parents)
  const rootNodes = nodes.filter((n) => !n.parentId || n.parentId === null);
  rootNodes.forEach((rootNode) => {
    processNode(rootNode.id);
  });

  return lines.join("\n");
});

// Get the full selected node object for the sidebar
const selectedNode = computed(() => {
  if (!selectedNodeId.value) return null;
  return mindmapStore.nodes.get(selectedNodeId.value) || null;
});

// Drag and Drop handlers
const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "copy";
  }
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();

  try {
    const jsonData = event.dataTransfer?.getData("application/json");
    const itemType = event.dataTransfer?.getData("item-type");

    if (!jsonData) {
      console.warn("No JSON data in drop event");
      return;
    }

    if (!canvasContainer.value) return;

    // Calculate position accounting for zoom and pan
    const canvasX = (event.clientX - panX.value) / zoom.value;
    const canvasY = (event.clientY - panY.value) / zoom.value;

    const x = canvasX - 100; // Center the node
    const y = canvasY - 50;

    // Check if it's a category or variable
    if (itemType === "category") {
      handleCategoryDrop(jsonData, x, y);
    } else {
      // Handle as variable (default)
      const variable: Variable = JSON.parse(jsonData);
      const newNode = mindmapStore.createRootNode(variable, { x, y });

      toast.success(`Node "${newNode.title}" created!`, {
        position: "top-right",
        autoClose: 2000,
      });

      console.log("Dropped variable on canvas:", variable, "at position:", {
        x,
        y,
      });
    }
  } catch (error) {
    console.error("Error handling drop:", error);
    toast.error("Failed to create node", {
      position: "top-right",
      autoClose: 3000,
    });
  }
};

// Category drop handler
const handleCategoryDrop = (jsonData: string, x: number, y: number) => {
  try {
    const data = JSON.parse(jsonData);
    const { category, variables } = data;

    if (!category) {
      console.error("Invalid category data");
      return;
    }

    // Create parent node for the category
    const categoryVariable: Variable = {
      _id: category._id,
      prompt_category_id: category._id,
      title: category.title,
      description: `Category with ${variables?.length || 0} variables`,
      type_id: "",
      icon: null,
      slug: category.title.toLowerCase().replace(/\s+/g, "-"),
      filterable: false,
      visible_on_card: false,
      created_at: new Date().toISOString(),
      updated_at: null,
      deleted_at: null,
      updated_by: null,
      deleted_by: null,
      is_trash: false,
      __v: 0,
    };

    const parentNode = mindmapStore.createRootNode(categoryVariable, { x, y });

    // Create child nodes for each variable
    if (variables && Array.isArray(variables) && variables.length > 0) {
      variables.forEach((variable: Variable, index: number) => {
        const childX = x + 250; // Position to the right of parent
        const childY = y + index * 120; // Stack vertically

        mindmapStore.createChildNode(variable, parentNode.id, {
          x: childX,
          y: childY,
        });
      });

      toast.success(
        `Category "${category.title}" created with ${variables.length} variables!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } else {
      toast.success(`Category "${category.title}" created!`, {
        position: "top-right",
        autoClose: 2000,
      });
    }

    console.log(
      "Dropped category:",
      category.title,
      "with",
      variables?.length || 0,
      "variables"
    );
  } catch (error) {
    console.error("Error creating category nodes:", error);
    toast.error("Failed to create category", {
      position: "top-right",
      autoClose: 3000,
    });
  }
};

// Node drag handlers
const handleNodeMouseDown = (event: MouseEvent, nodeId: string) => {
  // Don't start node dragging if we're connecting from an anchor
  if (connectingFrom.value) return;

  const node = mindmapStore.getNodeById(nodeId);
  if (!node) return;

  draggedNodeId.value = nodeId;

  // Calculate offset accounting for current zoom and pan
  const canvasX = (event.clientX - panX.value) / zoom.value;
  const canvasY = (event.clientY - panY.value) / zoom.value;

  dragOffset.value = {
    x: canvasX - node.x,
    y: canvasY - node.y,
  };

  event.preventDefault();
  event.stopPropagation();
};

const handleMouseMove = (event: MouseEvent) => {
  if (!draggedNodeId.value) return;

  // Calculate position accounting for zoom and pan
  const canvasX = (event.clientX - panX.value) / zoom.value;
  const canvasY = (event.clientY - panY.value) / zoom.value;

  const x = canvasX - dragOffset.value.x;
  const y = canvasY - dragOffset.value.y;

  mindmapStore.updateNodePosition(draggedNodeId.value, { x, y });
};

const handleMouseUp = () => {
  draggedNodeId.value = null;
};

// Node click handler
const handleNodeClick = (nodeId: string) => {
  mindmapStore.selectNode(nodeId);
};

// Canvas click handler
const handleCanvasClick = () => {
  mindmapStore.selectNode(null);
};

// Deselect node (for properties panel close button)
const handleDeselectNode = () => {
  mindmapStore.selectNode(null);
};

// Node drag over for creating child nodes
const handleNodeDragOver = (event: DragEvent, nodeId: string) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "link";
  }
};

// Node drop handler for creating child nodes
const handleNodeDrop = (event: DragEvent, parentId: string) => {
  event.preventDefault();

  try {
    const jsonData = event.dataTransfer?.getData("application/json");
    if (!jsonData) return;

    const variable: Variable = JSON.parse(jsonData);

    const parent = mindmapStore.getNodeById(parentId);
    if (!parent) return;

    // Position child node to the right of parent
    const x = parent.x + parent.width + 150;
    const y = parent.y + parent.children.length * 100;

    const newNode = mindmapStore.createChildNode(variable, parentId, { x, y });

    if (newNode) {
      toast.success(`Child node "${newNode.title}" created!`, {
        position: "top-right",
        autoClose: 2000,
      });
    }
  } catch (error) {
    console.error("Error creating child node:", error);
  }
};

// Delete node handler
const handleDeleteNode = (nodeId: string) => {
  const node = mindmapStore.getNodeById(nodeId);
  if (!node) return;

  // Prevent deletion of non-deletable nodes (like Start node)
  if (node.isDeletable === false) {
    toast.warning("This node cannot be deleted", {
      position: "top-right",
      autoClose: 2000,
    });
    return;
  }

  if (
    confirm(
      `Are you sure you want to delete "${node.title}" and all its children?`
    )
  ) {
    mindmapStore.deleteNode(nodeId);
    toast.info("Node deleted", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// Helper to check if node is a category
const isCategory = (node: any) => {
  return node.description && node.description.includes("Category with");
};

// Toggle node collapse/expand
const handleToggleCollapse = (nodeId: string) => {
  mindmapStore.toggleNodeCollapse(nodeId);
};

// Get count of variable children that are hidden when a category is collapsed
const getCollapsedVariableCount = (node: any): number => {
  if (!node.children || node.children.length === 0) {
    return 0;
  }

  const allNodes = mindmapStore.allNodes;
  let count = 0;

  for (const childId of node.children) {
    const childNode = allNodes.find((n) => n.id === childId);
    if (childNode) {
      // Only count if child is NOT a category
      const isChildCategory =
        childNode.description &&
        childNode.description.includes("Category with");
      if (!isChildCategory) {
        count++;
      }
    }
  }

  return count;
};

// Copy final prompt to clipboard
const copyFinalPrompt = async () => {
  if (!finalPrompt.value) {
    toast.warning("No prompt to copy", {
      position: "top-right",
      autoClose: 2000,
    });
    return;
  }

  try {
    await navigator.clipboard.writeText(finalPrompt.value);
    toast.success("Prompt copied to clipboard!", {
      position: "top-right",
      autoClose: 2000,
    });
  } catch (err) {
    console.error("Failed to copy:", err);
    toast.error("Failed to copy prompt", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// ── Format AI markdown-like response to HTML ──────────────────────────────────
const formattedAiResponse = computed(() => {
  if (!aiResponse.value) return "";
  let text = aiResponse.value;

  // Code blocks (``` ... ```)
  text = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="ai-code-block"><code class="lang-${lang || 'text'}">${escapeHtml(code.trim())}</code></pre>`;
  });

  // Inline code
  text = text.replace(/`([^`]+)`/g, '<code class="ai-inline-code">$1</code>');

  // Bold (**text**)
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic (*text*)
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Headers (### ## #)
  text = text.replace(/^### (.+)$/gm, '<h5 class="ai-h5">$1</h5>');
  text = text.replace(/^## (.+)$/gm, '<h4 class="ai-h4">$1</h4>');
  text = text.replace(/^# (.+)$/gm, '<h3 class="ai-h3">$1</h3>');

  // Unordered lists
  text = text.replace(/^[-*] (.+)$/gm, '<li>$1</li>');
  text = text.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  // Numbered lists
  text = text.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Line breaks
  text = text.replace(/\n\n/g, '</p><p>');
  text = text.replace(/\n/g, '<br/>');
  text = `<p>${text}</p>`;

  return text;
});

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Handle Run Prompt button click from toolbar
const handleRunPromptClick = () => {
  if (sidebarRef.value) {
    sidebarRef.value.setActiveTab("chat");
  }
  runFinalPrompt();
};


// ── Run prompt via OpenAI streaming API ───────────────────────────────────────
const runFinalPrompt = async () => {
  if (!finalPrompt.value) {
    toast.warning("No prompt to run. Add nodes to the canvas first.", {
      position: "top-right",
      autoClose: 3000,
    });
    return;
  }

  // Reset previous response
  aiResponse.value = "";
  responseTokenCount.value = 0;
  isRunningPrompt.value = true;

  // Scroll to response panel
  await nextTick();
  aiResponseContent.value?.scrollIntoView({ behavior: "smooth", block: "start" });

  abortController = new AbortController();

  try {
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.VUE_APP_OPENAI_API_KEY}`,
      },
      signal: abortController.signal,
      body: JSON.stringify({
        model: selectedModel.value,
        stream: true,
        messages: [
          {
            role: "system",
            content:
              "You are a helpful AI assistant. Format your responses clearly using markdown where appropriate.",
          },
          {
            role: "user",
            content: finalPrompt.value,
          },
        ],
      }),
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(
        errData?.error?.message || `API error: ${response.status}`
      );
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder("utf-8");

    if (!reader) throw new Error("No response body");

    let charCount = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n").filter((l) => l.trim());

      for (const line of lines) {
        if (line === "data: [DONE]") break;
        if (!line.startsWith("data: ")) continue;

        try {
          const json = JSON.parse(line.slice(6));
          const delta = json?.choices?.[0]?.delta?.content;
          if (delta) {
            aiResponse.value += delta;
            charCount += delta.length;
            responseTokenCount.value = Math.ceil(charCount / 4); // rough estimate

            // Auto-scroll response panel
            await nextTick();
            if (aiResponseContent.value) {
              aiResponseContent.value.scrollTop =
                aiResponseContent.value.scrollHeight;
            }
          }
        } catch {
          // skip malformed chunks
        }
      }
    }

    toast.success("AI response generated!", {
      position: "top-right",
      autoClose: 2000,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      toast.info("Generation stopped.", {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      console.error("OpenAI error:", err);
      toast.error(err?.message || "Failed to get AI response", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  } finally {
    isRunningPrompt.value = false;
    abortController = null;
  }
};

// Stop streaming
const stopPromptRun = () => {
  abortController?.abort();
};

// Copy AI response
const copyAiResponse = async () => {
  if (!aiResponse.value) return;
  try {
    await navigator.clipboard.writeText(aiResponse.value);
    toast.success("AI response copied!", {
      position: "top-right",
      autoClose: 2000,
    });
  } catch {
    toast.error("Failed to copy response", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// Clear AI response
const clearAiResponse = () => {
  aiResponse.value = "";
  responseTokenCount.value = 0;
};

// Show add child modal
const showAddChildModal = (nodeId: string) => {
  const parentNode = mindmapStore.getNodeById(nodeId);
  if (!parentNode) return;

  // Find the category ID from the parent node
  // If parent is a category, use its variableId as the category ID
  const categoryId = parentNode.variableId || "";

  parentNodeIdForNewChild.value = nodeId;
  newNodeData.value.prompt_category_id = categoryId;
  showAddNodeModal.value = true;
};

// Handle add child from panel
const handleAddChildNodeFromPanel = (nodeId: string) => {
  showAddChildModal(nodeId);
};

// Close add node modal
const closeAddNodeModal = () => {
  showAddNodeModal.value = false;
  parentNodeIdForNewChild.value = null;
  newNodeData.value = {
    title: "",
    slug: "",
    description: "",
    prompt_category_id: "",
    type_id: "",
    filterable: false,
    visible_on_card: false,
  };
};

// Submit new node
const submitNewNode = async () => {
  if (!parentNodeIdForNewChild.value) return;

  isCreatingNode.value = true;
  try {
    // Create variable via API
    const response = await apiService.post(
      "/common/prompt-variables",
      newNodeData.value
    );

    // Get parent node to calculate child position
    const parentNode = mindmapStore.getNodeById(parentNodeIdForNewChild.value);
    if (!parentNode) {
      throw new Error("Parent node not found");
    }

    // Calculate position for new child (below and to the right of parent)
    const childPosition = {
      x: parentNode.x + parentNode.width + 50,
      y: parentNode.y + parentNode.children.length * 120,
    };

    // Create the child node in the mindmap
    const variable: Variable = {
      _id: response._id,
      prompt_category_id: response.prompt_category_id,
      title: response.title,
      description: response.description || "",
      type_id: response.type_id || "",
      icon: response.icon || null,
      slug: response.slug,
      filterable: response.filterable || false,
      visible_on_card: response.visible_on_card || false,
      created_at: response.created_at,
      updated_at: response.updated_at,
      deleted_at: null,
      updated_by: null,
      deleted_by: null,
      is_trash: false,
      __v: 0,
    };

    mindmapStore.createChildNode(
      variable,
      parentNodeIdForNewChild.value,
      childPosition
    );

    toast.success("Child node created successfully", {
      position: "top-right",
      autoClose: 2000,
    });

    closeAddNodeModal();
  } catch (error: any) {
    console.error("Error creating node:", error);
    toast.error(error?.response?.data?.message || "Failed to create node", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    isCreatingNode.value = false;
  }
};

// Load categories and types
const loadCategoriesAndTypes = async () => {
  try {
    const [categoriesResponse, typesResponse] = await Promise.all([
      apiService.get("/common/prompt-categories"),
      apiService.get("/common/variable-types"),
    ]);
    categories.value = categoriesResponse || [];
    variableTypes.value = typesResponse || [];
  } catch (error) {
    console.error("Error loading categories/types:", error);
  }
};

// Clear canvas handler
const handleClearCanvas = () => {
  if (nodeCount.value === 0) return;

  if (confirm("Are you sure you want to clear the entire canvas?")) {
    mindmapStore.clearCanvas();
    toast.info("Canvas cleared", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// Zoom handlers
const handleZoomIn = () => {
  zoom.value = Math.min(maxZoom, zoom.value + zoomStep);
};

const handleZoomOut = () => {
  zoom.value = Math.max(minZoom, zoom.value - zoomStep);
};

const handleResetZoom = () => {
  zoom.value = 1;
};

const handleWheel = (event: WheelEvent) => {
  // Prevent the browser from scrolling the page
  event.preventDefault();
  event.stopPropagation();

  if (event.ctrlKey || event.metaKey) {
    // Ctrl/Cmd + scroll (or pinch gesture which browsers map to ctrlKey+wheel) → ZOOM
    const delta = event.deltaY > 0 ? -zoomStep : zoomStep;
    zoom.value = Math.max(minZoom, Math.min(maxZoom, zoom.value + delta));
  } else {
    // Regular two-finger scroll → PAN
    panX.value -= event.deltaX;
    panY.value -= event.deltaY;
  }
};

const handleFitToScreen = () => {
  // Toggle fullscreen state
  isFullScreen.value = !isFullScreen.value;

  if (nodeCount.value === 0) {
    zoom.value = 1;
    return;
  }

  // Calculate bounding box of all nodes
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  allNodes.value.forEach((node) => {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.width);
    maxY = Math.max(maxY, node.y + node.height);
  });

  if (!canvasContainer.value) return;

  // Wait for next tick to ensure container Rect is updated if fullscreen changed
  setTimeout(() => {
    if (!canvasContainer.value) return;
    const containerRect = canvasContainer.value.getBoundingClientRect();
    const contentWidth = maxX - minX + 100; // Add padding
    const contentHeight = maxY - minY + 100;

    const scaleX = containerRect.width / contentWidth;
    const scaleY = containerRect.height / contentHeight;

    zoom.value = 1;
  }, 100);
};

// Pan handlers
const handlePanStart = (event: MouseEvent) => {
  // Don't pan if dragging a node or connecting
  if (draggedNodeId.value) return;
  if (connectingFrom.value) return;

  // Pan with left-click drag on empty canvas, OR middle mouse button
  if (event.button === 0 || event.button === 1) {
    isPanning.value = true;
    panStart.value = {
      x: event.clientX - panX.value,
      y: event.clientY - panY.value,
    };
    event.preventDefault();
  }
};

const handlePanMove = (event: MouseEvent) => {
  if (!isPanning.value) return;

  panX.value = event.clientX - panStart.value.x;
  panY.value = event.clientY - panStart.value.y;
};

const handlePanEnd = () => {
  isPanning.value = false;
};

const handleResetView = () => {
  zoom.value = 1;
  panX.value = 0;
  panY.value = 0;
  toast.info("View reset", {
    position: "top-right",
    autoClose: 1500,
  });
};

// Connection mode handlers
const handleAnchorClick = (nodeId: string) => {
  if (!connectingFrom.value) {
    // First click - start connection
    connectingFrom.value = nodeId;
    toast.info("Click another node to connect", {
      position: "top-right",
      autoClose: 2000,
    });
  } else if (connectingFrom.value === nodeId) {
    // Clicked same node - cancel
    connectingFrom.value = null;
    toast.info("Connection cancelled", {
      position: "top-right",
      autoClose: 1500,
    });
  } else {
    // Second click - create connection
    const sourceId = connectingFrom.value;
    const targetId = nodeId;

    // Check if connection already exists
    const existingConnection = Array.from(
      mindmapStore.connections.values()
    ).find((conn) => conn.sourceId === sourceId && conn.targetId === targetId);

    if (existingConnection) {
      toast.warning("Connection already exists", {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      // Create the connection
      const result = mindmapStore.createManualConnection(sourceId, targetId);
      if (result) {
        toast.success("Connection created!", {
          position: "top-right",
          autoClose: 1500,
        });
      } else {
        toast.error("Failed to create connection", {
          position: "top-right",
          autoClose: 2000,
        });
      }
    }

    connectingFrom.value = null;
  }
};

// Connection handlers (drag-from-dot-to-drop-on-node)
const handleAnchorMouseDown = (event: MouseEvent, nodeId: string) => {
  event.preventDefault();
  event.stopPropagation();

  connectingFrom.value = nodeId;

  toast.info("Release on another node to connect", {
    position: "top-right",
    autoClose: 1500,
  });
};

const handleNodeMouseUp = (event: MouseEvent, nodeId: string) => {
  if (!connectingFrom.value) return;

  event.preventDefault();
  event.stopPropagation();

  if (connectingFrom.value === nodeId) {
    // Released on same node - cancel
    connectingFrom.value = null;
    toast.info("Connection cancelled", {
      position: "top-right",
      autoClose: 1500,
    });
    return;
  }

  // Create connection
  const sourceId = connectingFrom.value;
  const targetId = nodeId;

  // Check if connection already exists
  const existingConnection = Array.from(mindmapStore.connections.values()).find(
    (conn) => conn.sourceId === sourceId && conn.targetId === targetId
  );

  if (existingConnection) {
    toast.warning("Connection already exists", {
      position: "top-right",
      autoClose: 2000,
    });
  } else {
    const result = mindmapStore.createManualConnection(sourceId, targetId);
    if (result) {
      toast.success("Connection created!", {
        position: "top-right",
        autoClose: 1500,
      });
    } else {
      toast.error("Failed to create connection", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  }

  connectingFrom.value = null;
};

const handleConnectionClick = (connectionId: string) => {
  selectedConnectionId.value = connectionId;
  if (confirm("Delete this connection?")) {
    mindmapStore.deleteConnection(connectionId);
    selectedConnectionId.value = null;
    toast.info("Connection deleted", {
      position: "top-right",
      autoClose: 1500,
    });
  } else {
    selectedConnectionId.value = null;
  }
};

// Keyboard shortcuts
const handleKeyDown = (event: KeyboardEvent) => {
  // Don't handle keyboard shortcuts if user is typing in an input/textarea
  const target = event.target as HTMLElement;
  if (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  ) {
    return;
  }

  const selectedNode = mindmapStore.selectedNode;
  const nodes = allNodes.value;

  // Tab key to cycle through category nodes
  if (event.key === "Tab") {
    event.preventDefault();
    const categoryNodes = nodes.filter((node) => isCategory(node));

    if (categoryNodes.length === 0) return;

    if (!selectedNode) {
      // Select first category
      mindmapStore.selectNode(categoryNodes[0].id);
    } else {
      const currentIndex = categoryNodes.findIndex(
        (n) => n.id === selectedNode.id
      );

      if (currentIndex === -1) {
        // Current selection is not a category, select first category
        mindmapStore.selectNode(categoryNodes[0].id);
      } else {
        // Cycle to next category (or loop back to first)
        const nextIndex = event.shiftKey
          ? (currentIndex - 1 + categoryNodes.length) % categoryNodes.length // Shift+Tab goes backward
          : (currentIndex + 1) % categoryNodes.length; // Tab goes forward
        mindmapStore.selectNode(categoryNodes[nextIndex].id);
      }
    }
    return;
  }

  // Arrow keys to navigate between nodes
  if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
    event.preventDefault();

    if (!selectedNode) {
      // No node selected, select the first one
      if (nodes.length > 0) {
        mindmapStore.selectNode(nodes[0].id);
      }
      return;
    }

    const currentNode = mindmapStore.getNodeById(selectedNode.id);
    if (!currentNode) return;

    // Find the closest node in the direction of the arrow key
    let closestNode = null;
    let closestDistance = Infinity;

    nodes.forEach((node) => {
      if (node.id === selectedNode.id) return;

      const dx = node.x - currentNode.x;
      const dy = node.y - currentNode.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Check if node is in the right direction
      let isInDirection = false;
      const threshold = 50; // Tolerance for directional navigation

      switch (event.key) {
        case "ArrowUp":
          isInDirection = dy < -threshold && Math.abs(dx) < Math.abs(dy);
          break;
        case "ArrowDown":
          isInDirection = dy > threshold && Math.abs(dx) < Math.abs(dy);
          break;
        case "ArrowLeft":
          isInDirection = dx < -threshold && Math.abs(dy) < Math.abs(dx);
          break;
        case "ArrowRight":
          isInDirection = dx > threshold && Math.abs(dy) < Math.abs(dx);
          break;
      }

      if (isInDirection && distance < closestDistance) {
        closestDistance = distance;
        closestNode = node;
      }
    });

    if (closestNode) {
      mindmapStore.selectNode(closestNode.id);
    }
    return;
  }

  // Delete/Backspace to delete selected node
  if ((event.key === "Delete" || event.key === "Backspace") && selectedNode) {
    event.preventDefault();
    handleDeleteNode(selectedNode.id);
    return;
  }

  // Escape to cancel connection or deselect node
  if (event.key === "Escape") {
    if (connectingFrom.value) {
      connectingFrom.value = null;
      toast.info("Connection cancelled", {
        position: "top-right",
        autoClose: 1500,
      });
    } else if (selectedNode) {
      mindmapStore.selectNode(null);
    }
    return;
  }

  // Delete selected connection with Delete key (only if no node is selected)
  if (event.key === "Delete" && !selectedNode && selectedConnectionId.value) {
    mindmapStore.deleteConnection(selectedConnectionId.value);
    selectedConnectionId.value = null;
    toast.info("Connection deleted", {
      position: "top-right",
      autoClose: 1500,
    });
  }

  // Ctrl+Enter to add child node (only for category nodes)
  if (event.ctrlKey && event.key === "Enter" && selectedNode) {
    const node = mindmapStore.getNodeById(selectedNode.id);
    if (node && isCategory(node)) {
      event.preventDefault();
      showAddChildModal(selectedNode.id);
    }
  }
};

// Save flow to database
const handleSaveFlow = async () => {
  if (!flowTitle.value) {
    flowTitle.value = prompt("Enter a title for this flow:") || "Untitled Flow";
    if (!flowTitle.value || flowTitle.value === "Untitled Flow") {
      toast.warning("Please provide a flow title", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }
  }

  isSavingFlow.value = true;
  try {
    const flowData = {
      title: flowTitle.value,
      description: flowDescription.value,
      nodes: mindmapStore.allNodes,
      connections: Array.from(mindmapStore.connections.values()),
      canvas_settings: {
        offset_x: panX.value,
        offset_y: panY.value,
        scale: zoom.value,
      },
      metadata: {
        node_count: mindmapStore.nodeCount,
        connection_count: mindmapStore.connectionCount,
      },
    };

    console.log("Saving flow with nodes:", flowData.nodes.length);
    console.log(
      "Start node exists?",
      flowData.nodes.some((n) => n.id === "start_node")
    );

    let response;
    if (currentFlowId.value) {
      // Update existing flow
      response = await apiService.put(
        "/common/prompt-flows",
        currentFlowId.value,
        flowData
      );
      toast.success("Flow updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      // Create new flow
      response = await apiService.post("/common/prompt-flows", flowData);
      if (response.data && response.data._id) {
        currentFlowId.value = response.data._id;
        // Update URL with flowId
        router.replace({
          name: "Mindmap",
          query: { flowId: currentFlowId.value },
        });
      }
      toast.success("Flow saved successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  } catch (error) {
    console.error("Error saving flow:", error);
    toast.error("Failed to save flow", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    isSavingFlow.value = false;
  }
};

// Load flow from database
const loadFlow = async (flowId: string) => {
  isLoadingFlow.value = true;
  try {
    const response = await apiService.get(`/common/prompt-flows/${flowId}`);
    console.log("API Response:", response);
    const flowData = response;
    console.log("Flow Data:", flowData);

    if (flowData) {
      // Set flow metadata
      currentFlowId.value = flowData._id;
      flowTitle.value = flowData.title || "";
      flowDescription.value = flowData.description || "";

      console.log("Flow nodes from API:", flowData.nodes);
      console.log("Number of nodes from API:", flowData.nodes?.length || 0);

      // Import mindmap data
      const importData = {
        nodes: flowData.nodes || [],
        connections: flowData.connections || [],
        metadata: flowData.metadata || {},
      };

      console.log("Import data:", importData);
      const success = mindmapStore.importMindmap(importData);

      console.log(
        "After import - Total nodes in store:",
        mindmapStore.allNodes.length
      );
      console.log(
        "Nodes after import:",
        mindmapStore.allNodes.map((n) => ({ id: n.id, title: n.title }))
      );

      // Restore canvas settings
      if (flowData.canvas_settings) {
        panX.value = flowData.canvas_settings.offset_x || 0;
        panY.value = flowData.canvas_settings.offset_y || 0;
        zoom.value = flowData.canvas_settings.scale || 1;
      }

      if (success) {
        toast.success("Flow loaded successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      }
    }
  } catch (error) {
    console.error("Error loading flow:", error);
    toast.error("Failed to load flow", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    isLoadingFlow.value = false;
  }
};

// Navigate back to flows list
const handleBackToList = () => {
  router.push({ name: "prompt-flows" });
};

// Export handler
const handleExport = () => {
  const data = mindmapStore.exportMindmap();
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `mindmap-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);

  toast.success("Mindmap exported!", {
    position: "top-right",
    autoClose: 2000,
  });
};

// Import handler
const handleImport = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "application/json";
  input.onchange = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        const success = mindmapStore.importMindmap(data);

        if (success) {
          toast.success("Mindmap imported successfully!", {
            position: "top-right",
            autoClose: 2000,
          });
        } else {
          toast.error("Failed to import mindmap", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Error importing mindmap:", error);
        toast.error("Invalid mindmap file", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    };
    reader.readAsText(file);
  };
  input.click();
};

// Connection path calculation
const getConnectionPath = (connection: any) => {
  const source = mindmapStore.getNodeById(connection.sourceId);
  const target = mindmapStore.getNodeById(connection.targetId);

  if (!source || !target) return "";

  const x1 = source.x + source.width;
  const y1 = source.y + source.height / 2;
  const x2 = target.x;
  const y2 = target.y + target.height / 2;

  const midX = (x1 + x2) / 2;

  return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
};

onMounted(async () => {
  console.log("Mindmap component mounted");

  // Load categories and types for add node modal
  loadCategoriesAndTypes();

  // Check if there's a flowId in the URL query
  const flowId = route.query.flowId as string;
  if (flowId) {
    // Load existing flow from database (importMindmap ensures start node exists)
    await loadFlow(flowId);
  } else {
    // For new flow, ensure start node exists
    if (!mindmapStore.nodes.get("start_node")) {
      console.log("Creating start node for new flow");
      mindmapStore.createStartNode();
    }
  }

  // Add global mouse event listeners for node dragging and panning
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  document.addEventListener("mousemove", handlePanMove);
  document.addEventListener("mouseup", handlePanEnd);

  // Add keyboard shortcuts
  document.addEventListener("keydown", handleKeyDown);

  // Attach wheel listener with { passive: false } so we can call preventDefault()
  // We must use this instead of @wheel.prevent on the template for proper touch support
  const wrapper = canvasWrapper.value;
  if (wrapper) {
    wrapper.addEventListener("wheel", handleWheel, { passive: false });
  }
});

onUnmounted(() => {
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
  document.removeEventListener("mousemove", handlePanMove);
  document.removeEventListener("mouseup", handlePanEnd);
  document.removeEventListener("keydown", handleKeyDown);

  const wrapper = canvasWrapper.value;
  if (wrapper) {
    wrapper.removeEventListener("wheel", handleWheel);
  }
});
</script>

<style scoped>
/* ===== GOOGLE FONTS ===== */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* ===== CONTAINER ===== */
.mindmap-container {
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f0f2f5;
}

/* ===== TOOLBAR ===== */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, #1a1f36 0%, #2d3561 100%);
  border-radius: 12px 12px 0 0;
  gap: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(26, 31, 54, 0.3);
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.toolbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.flow-title-badge {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 0.35rem 0.85rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  backdrop-filter: blur(4px);
}

.toolbar-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 0.25rem;
}

/* Toolbar Buttons */
.toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.toolbar-btn--icon {
  padding: 0.45rem 0.65rem;
}

.toolbar-btn--primary {
  background: linear-gradient(135deg, #5c6ac4 0%, #4053b8 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(92, 106, 196, 0.4);
}

.toolbar-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #6a78d0 0%, #4e63cc 100%);
  box-shadow: 0 4px 14px rgba(92, 106, 196, 0.5);
  transform: translateY(-1px);
}

.toolbar-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toolbar-btn--ghost {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.toolbar-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.toolbar-btn--danger-ghost {
  background: rgba(245, 101, 101, 0.15);
  color: #fc8181;
  border: 1px solid rgba(245, 101, 101, 0.25);
}

.toolbar-btn--danger-ghost:hover {
  background: rgba(245, 101, 101, 0.25);
  color: #feb2b2;
  border-color: rgba(245, 101, 101, 0.4);
}

.toolbar-btn--light {
  background: #ffffff;
  color: #374151;
  border: 1.5px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.toolbar-btn--light:hover {
  background: #f9fafb;
  color: #111827;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

/* Stats Group */
.stats-group {
  display: flex;
  gap: 0.5rem;
}

.stat-chip {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
}

.stat-chip i {
  font-size: 0.7rem;
  opacity: 0.8;
}

.stat-chip small {
  font-weight: 400;
  opacity: 0.7;
  font-size: 0.68rem;
}

.stat-chip--blue {
  background: rgba(99, 179, 237, 0.15);
  color: #90cdf4;
  border: 1px solid rgba(99, 179, 237, 0.2);
}

.stat-chip--green {
  background: rgba(104, 211, 145, 0.15);
  color: #9ae6b4;
  border: 1px solid rgba(104, 211, 145, 0.2);
}

.stat-chip--purple {
  background: rgba(183, 148, 246, 0.15);
  color: #d6bcfa;
  border: 1px solid rgba(183, 148, 246, 0.2);
  cursor: pointer;
}

/* ===== MINDMAP WRAPPER ===== */
.mindmap-wrapper {
  display: flex;
  flex: 1;
  overflow: hidden;
  border-radius: 0 0 12px 12px;
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.mindmap-wrapper.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2000;
  border-radius: 0;
  box-shadow: none;
}

/* ===== CANVAS WRAPPER ===== */
.canvas-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #f7f8fc;
  touch-action: none; /* Prevent browser default touch/gesture handling */
}

/* ===== CANVAS AREA ===== */
.canvas-area {
  width: 100%;
  height: 100%;
  min-width: 10000px;
  min-height: 10000px;
  position: relative;
  background-color: #f7f8fc;
  background-image:
    radial-gradient(circle, rgba(99, 102, 241, 0.12) 1px, transparent 1px);
  background-size: 28px 28px;
  will-change: transform;
}

/* ===== CANVAS CONTROLS ===== */
.canvas-controls {
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06);
  z-index: 100;
  backdrop-filter: blur(8px);
}

.controls-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

.controls-separator {
  width: 30px;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 0.1rem 0;
}

.ctrl-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.ctrl-btn:hover {
  background: #6366f1;
  color: #fff;
  border-color: #6366f1;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
  transform: scale(1.05);
}

.ctrl-btn--active {
  background: #6366f1;
  color: #fff;
  border-color: #6366f1;
}

.zoom-level-display {
  font-size: 0.68rem;
  font-weight: 700;
  color: #6366f1;
  width: 34px;
  text-align: center;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 4px;
  transition: all 0.2s;
  letter-spacing: -0.02em;
}

.zoom-level-display:hover {
  background: rgba(99, 102, 241, 0.1);
}

.gesture-hint {
  font-size: 0.6rem;
  color: #9ca3af;
  text-align: center;
  padding: 0.2rem 0.3rem;
  width: 100%;
}

.hint-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  justify-content: flex-start;
  padding: 0.1rem 0;
}

.hint-row i {
  width: 12px;
  text-align: center;
  opacity: 0.7;
}

/* ===== CANVAS PLACEHOLDER ===== */
#mindmap-canvas {
  width: 100%;
  height: 100%;
  display: none;
}

.canvas-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  color: #9ca3af;
}

.placeholder-icon {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  font-size: 2rem;
  color: #6366f1;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.2);
}

.canvas-placeholder h5 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.5rem;
}

.canvas-placeholder p {
  font-size: 0.85rem;
  color: #9ca3af;
  margin-bottom: 1.25rem;
}

.placeholder-hints {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}

.hint-item {
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.hint-item kbd {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.15rem 0.45rem;
  font-size: 0.7rem;
  font-family: inherit;
  color: #374151;
  box-shadow: 0 1px 0 rgba(0,0,0,0.12);
}

/* ===== MINDMAP NODES ===== */
.mindmap-node {
  position: absolute;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  cursor: default;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.05);
  user-select: none;
  z-index: 2;
  min-width: 180px;
}

.mindmap-node:hover {
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.15), 0 2px 6px rgba(0, 0, 0, 0.06);
  border-color: #a5b4fc;
  transform: translateY(-1px);
}

.mindmap-node.selected {
  border-color: #6366f1;
  border-width: 2px;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15), 0 8px 24px rgba(99, 102, 241, 0.2);
  transform: translateY(-1px);
}

.mindmap-node.root {
  border-width: 2px;
  border-color: #6366f1;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
}

.mindmap-node.connect-source {
  border-color: #22c55e;
  border-width: 2px;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.15);
}

.mindmap-node.connect-target:hover {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.2);
}

/* ===== NODE COMPONENTS ===== */
.node-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.drag-handle {
  color: #d1d5db;
  font-size: 0.8rem;
  cursor: grab;
  transition: color 0.15s;
}

.mindmap-node:hover .drag-handle {
  color: #9ca3af;
}

.mindmap-node:active .drag-handle {
  cursor: grabbing;
}

.node-title {
  flex: 1;
  font-size: 0.825rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.node-actions {
  display: flex;
  gap: 0.2rem;
}

.node-delete,
.node-add-child,
.node-collapse {
  padding: 0.2rem 0.3rem;
  opacity: 0;
  transition: opacity 0.15s ease;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 1;
}

.mindmap-node:hover .node-delete,
.mindmap-node:hover .node-add-child,
.mindmap-node:hover .node-collapse {
  opacity: 1;
}

.node-description {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.node-footer {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.node-footer .badge {
  font-size: 0.62rem;
  border-radius: 6px;
  padding: 0.2em 0.5em;
}

/* ===== CONNECTION ANCHORS ===== */
.connection-anchor {
  position: absolute;
  width: 20px;
  height: 20px;
  cursor: crosshair;
  z-index: 10;
}

.anchor-left {
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
}

.anchor-right {
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
}

.anchor-dot {
  width: 10px;
  height: 10px;
  background: #6366f1;
  border: 2px solid #fff;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.2s ease;
  box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
}

.connection-anchor:hover .anchor-dot {
  width: 14px;
  height: 14px;
  background: #4f46e5;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.25);
}

/* ===== CONNECTIONS SVG LAYER ===== */
.connections-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.connection-path {
  cursor: pointer;
  pointer-events: stroke;
  transition: stroke 0.2s, stroke-width 0.2s;
}

.connection-path:hover {
  stroke: #6366f1 !important;
  stroke-width: 3 !important;
}

.connection-hit-area {
  cursor: pointer;
  pointer-events: stroke;
}

.connection-preview {
  pointer-events: none;
  animation: dash 1s linear infinite;
}

@keyframes dash {
  to { stroke-dashoffset: -10; }
}

/* ===== FINAL PROMPT PANEL ===== */
.final-prompt-panel {
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  height: 210px;
  min-height: 150px;
  max-height: 400px;
  flex-shrink: 0;
}

.final-prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1rem;
  background: linear-gradient(to right, #f9fafb, #f3f4f6);
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  gap: 0.75rem;
}

.prompt-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.prompt-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.prompt-header-left h6 {
  font-weight: 700;
  color: #111827;
  font-size: 0.9rem;
  margin: 0;
}

.prompt-header-left small {
  font-size: 0.72rem;
  margin: 0;
}

.prompt-header-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.prompt-char-count {
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 500;
}

.final-prompt-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.875rem 1rem;
}

.final-prompt-text {
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.825rem;
  line-height: 1.7;
  color: #374151;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.prompt-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  text-align: center;
}

.prompt-empty-state p {
  font-size: 0.82rem;
  margin: 0;
}

/* ===== SCROLLBARS ===== */
.final-prompt-content::-webkit-scrollbar {
  width: 6px;
}

.final-prompt-content::-webkit-scrollbar-track {
  background: transparent;
}

.final-prompt-content::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.final-prompt-content::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}


/* ===== RUN / STOP BUTTONS ===== */
.toolbar-btn--run {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
  animation: pulse-green 2s ease-in-out infinite;
}

.toolbar-btn--run:hover:not(:disabled) {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.5);
  transform: translateY(-1px);
  animation: none;
}

.toolbar-btn--run:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  animation: none;
}

@keyframes pulse-green {
  0%, 100% { box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35); }
  50% { box-shadow: 0 2px 18px rgba(16, 185, 129, 0.6); }
}

.toolbar-btn--stop {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.35);
}

.toolbar-btn--stop:hover {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.5);
  transform: translateY(-1px);
}

.toolbar-btn--ghost-dark {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.75);
  border: 1px solid rgba(255,255,255,0.12);
}

.toolbar-btn--ghost-dark:hover {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border-color: rgba(255,255,255,0.25);
}

/* AI Response scrollbar */
.ai-response-content::-webkit-scrollbar {
  width: 5px;
}

.ai-response-content::-webkit-scrollbar-track {
  background: transparent;
}

.ai-response-content::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

.ai-response-content::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}
</style>
